import {
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  Paper,
  Button,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useIntl } from "react-intl";
import { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import ModalComponent from "components/Modal";

interface Proveedor {
  id: number;
  razon_social: string;
  nombre_comercial: string;
  contacto: string;
  correo: string;
  telefono: string;
}

interface OrdenCompra {
  id: number;
  id_solicitud: number;
  numero: string;
  no_serie: string;
  proyecto_area: string;
  descripcion: string;
  computo?: string;
  estatus_orden: number;
  requisicion: number;
  proveedor: Proveedor;
  detalles: Detalles[];
}

interface Detalles {
  id: number;
  id_inventario: number;
  nombre_producto: string;
  cantidad: string;
  precio: string;
  precio_total: string;
  comentarios: string;
  estatus: number;
}

interface DetalleCanjeProps {
  verOrdenCompra?: OrdenCompra | null;
  usuarioArjion?: string | null;
  idUsuario?: number;
  nombreCompletoAutorizando?: string;
}

const DetallesOrdenCompra = ({
  verOrdenCompra,
  usuarioArjion,
  idUsuario,
  nombreCompletoAutorizando,
}: DetalleCanjeProps) => {
  if (!verOrdenCompra) return null;
  const intl = useIntl();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);

  const privilegiosToRol: Record<string, string> = {
    "froebel.ivan": "admin",
    "torres.tannya": "daf",
    "salazar.mario": "dg",
    "hernandez.lucero": "compras",
    "delarosa.brenda": "compras",
  };

  const tipoUsuarioArjion = privilegiosToRol[usuarioArjion as string] ?? "";

  const getEstadoColor = (
    estatus_requisicion: number
  ): "info" | "warning" | "primary" | "default" | "error" | "success" => {
    switch (estatus_requisicion) {
      case 1:
        return "info"; // Generada
      case 2:
        return "warning"; // Enviada
      case 4:
        return "success"; // Aceptada
      case 5:
        return "primary"; // Surtida
      case 6:
        return "error"; // Rechazada
      case 7:
        return "primary"; // XML Validado
      default:
        return "default";
    }
  };

  const estadosMap: { [key: number]: string } = {
    1: "Generada",
    2: "Enviada",
    4: "Aceptada",
    5: "Surtida",
    6: "Rechazada",
    7: "XML Validado",
  };

  // Función helper para traducir estados
  const traducirEstado = (estado: number | null): string => {
    if (estado === null || estado === undefined) return "SIN ESTADO";
    return estadosMap[estado] ?? "SIN ESTADO";
  };

  // Handlers para la paginación
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const detallesRequisicionPaginados = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return verOrdenCompra?.detalles.slice(startIndex, endIndex) || [];
  }, [verOrdenCompra?.detalles, page, rowsPerPage]);

  const totalPedido = useMemo(() => {
    return verOrdenCompra?.detalles?.reduce((sum, d) => sum + Number(d.precio_total), 0) ?? 0;
  }, [verOrdenCompra?.detalles]);

  return (
    <Box sx={{ px: 0, pb: 0, mt: 0 }}>
      <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12}>
          <Box sx={{ textAlign: "center", mb: 0 }}>
            <Typography variant="h5" gutterBottom>
              Detalle de la Orden de Compra #{verOrdenCompra.id}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            Estatus de la orden de compra
          </Typography>
          <Chip
            label={traducirEstado(verOrdenCompra.estatus_orden).toUpperCase()}
            color={getEstadoColor(verOrdenCompra.estatus_orden)}
            sx={{ fontWeight: "bold" }}
          />
        </Grid>
        {/* Info general */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ color: "#004c93" }}>
              Información del proveedor
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Nombre comercial
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor.nombre_comercial}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Razón Social
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor.razon_social}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Contacto
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor.contacto}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Correo
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor.correo}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verOrdenCompra.proveedor.telefono}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Detalles / productos */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ color: "#004c93", textAlign: "center" }}>
              Productos de la orden de compra ({verOrdenCompra.detalles?.length ?? 0})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {detallesRequisicionPaginados.map((detalle: Detalles, index: number) => (
              <Box
                key={detalle.id}
                sx={{ mb: 2, p: 1, border: "1px solid #e0e0e0", borderRadius: 1 }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Producto
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {detalle?.nombre_producto}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <Typography variant="body2" color="text.secondary">
                      Cantidad
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {detalle.cantidad}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <Typography variant="body2" color="text.secondary">
                      P. Unitario
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      $
                      {Number(detalle.precio).toLocaleString("es-MX", {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="primary" sx={{ color: "#004c93" }}>
                      $
                      {Number(detalle.precio_total).toLocaleString("es-MX", {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {/* Total */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mt: 1,
                p: 1,
                backgroundColor: "#e8f4fd",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                Total de la orden de compra:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="primary" sx={{ color: "#004c93" }}>
                ${totalPedido.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
              </Typography>
            </Box>

            {/* Paginación */}
            <TablePagination
              component="div"
              count={verOrdenCompra.detalles?.length ?? 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[3, 5, 10]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                ".MuiTablePagination-toolbar": {
                  justifyContent: "space-between",
                  padding: "8px 16px",
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  margin: 0,
                },
                ".MuiTablePagination-actions svg": { transform: "scaleX(-1)" },
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
export default DetallesOrdenCompra;
